import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SeverityPill } from "@/components/app/severity-pill";
import { DataAge } from "@/components/ui/sample-badge";
import { liveApi } from "@/lib/api/live";
import { ApiError, reportsApi } from "@/lib/api";
import { ReportSheet } from "@/components/app/ReportSheet";

export const Route = createFileRoute("/app/alerts")({
  head: () => ({
    meta: [{ title: "Alerts" }],
  }),
  component: AlertsTab,
});

function severityLabel(s: string) {
  const key = String(s || "").toUpperCase();
  if (key === "SEVERE") return "Severe — dangerous flooding";
  if (key === "WARNING") return "Warning — flooding likely";
  if (key === "WATCH") return "Watch — possible flooding";
  if (key === "LOW") return "Low risk";
  return key;
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, string> = {
    PENDING: t("report.statusPending"),
    APPROVED: t("report.statusApproved"),
    REJECTED: t("report.statusRejected"),
  };
  const color =
    status === "APPROVED"
      ? "text-green-600"
      : status === "REJECTED"
        ? "text-severe"
        : "text-muted-foreground";
  return (
    <span className={`text-xs font-semibold ${color}`}>
      {map[status] ?? status}
    </span>
  );
}

function AlertsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [expandedAlertId, setExpandedAlertId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showMyReports, setShowMyReports] = useState(false);

  const alertsQuery = useQuery({
    queryKey: ["user", "alerts"],
    queryFn: () => liveApi.userAlerts(),
    retry: 1,
    enabled: true,
    refetchInterval: 30_000,
  });

  const myReportsQuery = useQuery({
    queryKey: ["user", "my-reports"],
    queryFn: () => reportsApi.list(),
    retry: 1,
  });
  const myReports = myReportsQuery.data?.reports ?? [];

  const alerts = alertsQuery.data?.alerts ?? [];
  const officialWarnings = alertsQuery.data?.officialWarnings ?? [];
  const roadHazards = alertsQuery.data?.roadHazards ?? [];

  return (
    <div className="relative pb-20 space-y-5">
      <section className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="font-display text-base font-bold">Your alerts</h2>
          <DataAge
            at={
              alertsQuery.data?.fetchedAt
                ? new Date(alertsQuery.data.fetchedAt)
                : alertsQuery.dataUpdatedAt
                  ? new Date(alertsQuery.dataUpdatedAt)
                  : null
            }
          />
        </div>
        {alertsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading alerts…</p>
        ) : alertsQuery.isError ? (
          <p className="text-sm text-severe">
            {alertsQuery.error instanceof ApiError
              ? alertsQuery.error.message
              : "Could not load alerts."}
          </p>
        ) : alerts.length ? (
          <div className="space-y-3">
            {alerts.map((a) => {
              const open = expandedAlertId === a.id;
              const body = a.body || a.messageEn || a.title || "Alert";
              return (
                <button
                  key={a.id}
                  type="button"
                  className="w-full rounded-md border border-border bg-background p-3 text-left transition-colors hover:bg-muted/40"
                  onClick={() => setExpandedAlertId(open ? null : a.id)}
                  aria-expanded={open}
                >
                  <div className="flex items-start justify-between gap-2">
                    <SeverityPill severity={a.severity} />
                    <span className="text-right text-[10px] text-muted-foreground">
                      {new Date(a.sentAt || a.createdAt).toLocaleString()}
                      {a.validUntil ? (
                        <span className="mt-0.5 block">
                          Until {new Date(a.validUntil).toLocaleString()}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-bold">{a.title || body}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{severityLabel(a.severity)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {[a.county, ...(a.areas || [])].filter(Boolean).join(" · ")}
                  </p>
                  {open ? (
                    <div className="mt-3 space-y-2 border-t border-border pt-3">
                      <p className="text-sm leading-relaxed">{body}</p>
                      {a.advice ? (
                        <p className="text-sm font-medium text-foreground">
                          What to do: {a.advice}
                        </p>
                      ) : null}
                      <p className="text-[10px] font-semibold uppercase text-primary">
                        {a.isOfficial
                          ? `Official${a.officialSourceName ? ` · ${a.officialSourceName}` : ""}`
                          : "Advisory · not official"}
                      </p>
                      {a.officialSourceUrl ? (
                        <a
                          href={a.officialSourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="block text-xs font-semibold text-primary underline-offset-2 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View source
                        </a>
                      ) : null}
                    </div>
                  ) : (
                    <p className="mt-2 text-[10px] font-semibold uppercase text-primary">
                      {a.isOfficial ? "Official warning" : "Advisory · not official"} · Tap for
                      advice
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {alertsQuery.data?.locationRequired
              ? "Turn on location to see alerts for where you are."
              : "No active alerts where you are right now."}
          </p>
        )}
      </section>

      {officialWarnings.length ? (
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="font-display text-base font-bold">Official warnings</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            From government and partner feeds for your area.
          </p>
          <div className="mt-3 space-y-3">
            {officialWarnings.map((o) => (
              <article
                key={o.id}
                className="border-b border-border pb-3 last:border-0 last:pb-0"
              >
                <p className="text-sm font-bold">{o.headline}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {o.sourceName}
                  {o.receivedAt ? ` · ${new Date(o.receivedAt).toLocaleString()}` : ""}
                </p>
                <p className="mt-2 text-sm leading-relaxed">{o.body}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {roadHazards.length ? (
        <section className="rounded-lg border border-warning/40 bg-card p-4">
          <h2 className="font-display text-base font-bold">Impassable roads</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Crowd-reported blocked or flooded roads within 15 km of where you are. Avoid these routes.
          </p>
          <div className="mt-3 space-y-3">
            {roadHazards.map((r) => (
              <article key={r.id} className="rounded-md border border-border bg-background p-3">
                <div className="flex items-start justify-between gap-2">
                  <SeverityPill severity={r.severity || "WARNING"} />
                  <span className="text-[10px] text-muted-foreground">
                    {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}
                  </span>
                </div>
                <p className="mt-2 text-sm font-bold">{r.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {[r.placeLabel, r.ward, r.county].filter(Boolean).join(" · ")}
                </p>
                <p className="mt-2 text-sm leading-relaxed">{r.body}</p>
                {r.advice ? (
                  <p className="mt-2 text-sm font-medium">What to do: {r.advice}</p>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {/* My Reports collapsible section */}
      <section className="rounded-lg border border-border bg-card p-4">
        <button
          type="button"
          className="flex w-full items-center justify-between text-left"
          onClick={() => setShowMyReports(!showMyReports)}
          aria-expanded={showMyReports}
        >
          <span className="font-display text-base font-bold">
            {t("report.myReports")} ({myReports.length})
          </span>
          {showMyReports ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        {showMyReports && (
          <div className="mt-3 space-y-2">
            {myReports.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("report.noReports")}</p>
            ) : (
              myReports.map((r) => (
                <div key={r.id} className="rounded-md border border-border bg-background p-3">
                  <div className="flex items-start justify-between gap-2">
                    <SeverityPill severity={r.severity || "LOW"} />
                    <StatusBadge status={r.status} />
                  </div>
                  <p className="mt-1 text-sm font-medium">
                    {r.hazardType
                      ? t(`report.hazards.${r.hazardType}`)
                      : (r.category || "Report")}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {r.placeLabel || r.ward || ""}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* Floating Action Button */}
      <button
        type="button"
        aria-label={t("report.title")}
        className="fixed bottom-24 right-5 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-95"
        onClick={() => setSheetOpen(true)}
      >
        <Plus className="h-5 w-5" />
        {t("report.fab")}
      </button>

      <ReportSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onSuccess={(newReport) => {
          queryClient.setQueryData(
            ["user", "my-reports"],
            (old: { reports: typeof myReports } | undefined) => ({
              reports: [{ ...newReport, severity: newReport.severity ?? "WATCH" }, ...(old?.reports ?? [])],
            }),
          );
        }}
      />
    </div>
  );
}
